import { Injectable, TemplateRef, computed, signal } from '@angular/core';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | 'xxl';

export interface ModalOptions {
  centered?: boolean;
  size?: ModalSize;
  backdrop?: 'static' | true;
  keyboard?: boolean;
}

export interface ModalRef {
  close: (result?: unknown) => void;
  dismiss: (reason?: unknown) => void;
  result: Promise<unknown>;
}

interface ModalState {
  template: TemplateRef<unknown>;
  options: Required<ModalOptions>;
  ref: ModalRef;
}

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  private readonly defaultOptions: Required<ModalOptions> = {
    centered: true,
    size: 'lg',
    backdrop: 'static',
    keyboard: true,
  };

  private readonly modalStack = signal<ModalState[]>([]);

  readonly activeModals = computed(() => this.modalStack());
  readonly activeModal = computed(() => {
    const stack = this.modalStack();
    return stack.length ? stack[stack.length - 1] : null;
  });
  readonly isOpen = computed(() => this.modalStack().length > 0);

  private removeModal(modalRef: ModalRef): boolean {
    const stack = this.modalStack();
    const index = stack.findIndex((modal) => modal.ref === modalRef);

    if (index < 0) {
      return false;
    }

    this.modalStack.set(
      stack.filter((_, currentIndex) => currentIndex !== index),
    );
    return true;
  }

  open(template: TemplateRef<unknown>, options?: ModalOptions): ModalRef {
    const finalOptions: Required<ModalOptions> = {
      ...this.defaultOptions,
      ...options,
    };

    let resolveResult!: (value: unknown) => void;
    let rejectResult!: (reason?: unknown) => void;

    const result = new Promise<unknown>((resolve, reject) => {
      resolveResult = resolve;
      rejectResult = reject;
    });

    const modalRef: ModalRef = {
      close: (resultValue?: unknown) => {
        if (!this.removeModal(modalRef)) {
          return;
        }
        resolveResult(resultValue);
      },
      dismiss: (reason?: unknown) => {
        if (!this.removeModal(modalRef)) {
          return;
        }
        rejectResult(reason);
      },
      result,
    };

    this.modalStack.update((stack) => [
      ...stack,
      {
        template,
        options: finalOptions,
        ref: modalRef,
      },
    ]);

    return modalRef;
  }

  closeAll(): void {
    const currentStack = [...this.modalStack()];
    if (!currentStack.length) {
      return;
    }

    for (let index = currentStack.length - 1; index >= 0; index -= 1) {
      currentStack[index].ref.dismiss('dismiss-all');
    }
  }

  dismissActive(reason?: unknown): void {
    this.activeModal()?.ref.dismiss(reason);
  }

  closeActive(result?: unknown): void {
    this.activeModal()?.ref.close(result);
  }
}
